import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/context/AuthContext';
import { SEO } from '@/components/SEO';
import { PageHeader } from '@/components/layout';
import BottomNav from '@/components/BottomNav';
import { communeJsonLd } from '@/seo/jsonld';
import { COMMUNES, findCommuneBySlug } from '@/seo/communes';
import { MapPin, Calendar, AlertTriangle, Tag, Info, ArrowRight } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';

const TABS = [
  { id: 'alerte', label: 'Alertes', icon: AlertTriangle, color: 'text-red-500' },
  { id: 'evenement', label: 'Événements', icon: Calendar, color: 'text-secondary' },
  { id: 'bon_plan', label: 'Bons plans', icon: Tag, color: 'text-accent' },
  { id: 'info', label: 'Infos', icon: Info, color: 'text-primary' },
];

const CommunePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const commune = findCommuneBySlug(slug);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('alerte');

  useEffect(() => {
    if (!commune) return;
    setLoading(true);
    api.get('/posts', { params: { commune: commune.id, limit: 60 } })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [commune, api]);

  const byCategory = useMemo(() => {
    const now = Date.now();
    const groups = { alerte: [], evenement: [], bon_plan: [], info: [] };
    posts.forEach((p) => groups[p.category]?.push(p));
    groups.evenement = groups.evenement
      .filter((p) => !p.event_date || new Date(p.event_date).getTime() >= now - 86400000)
      .sort((a, b) => new Date(a.event_date || a.created_at) - new Date(b.event_date || b.created_at));
    return groups;
  }, [posts]);

  if (!commune) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-background" data-testid="commune-not-found">
        <SEO title="Commune introuvable – Bourbon" noindex />
        <h1 className="text-3xl font-bold">Commune introuvable</h1>
        <Link to="/" className="text-primary underline">Retour à l'accueil</Link>
      </div>
    );
  }

  const nearby = COMMUNES.filter((c) => c.region === commune.region && c.id !== commune.id);
  const title = `${commune.name} : alertes, événements et bons plans – Bourbon`;
  const description = `Toute l'actualité de ${commune.name} (${commune.region} de La Réunion) : ${byCategory.alerte.length} alertes, ${byCategory.evenement.length} événements à venir, bons plans et infos partagés par les habitants sur Bourbon.`;
  const list = byCategory[tab];

  return (
    <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
      <SEO title={title} description={description} path={`/commune/${commune.slug}`} jsonLd={communeJsonLd(commune, posts)} />

      <PageHeader back backTo={user ? '/feed' : '/'} title={commune.name} subtitle={`${commune.region} · La Réunion`} width="max-w-3xl"
        actions={!user && (
          <Button size="sm" className="rounded-full" onClick={() => navigate('/auth')} data-testid="commune-join-btn">Rejoindre</Button>
        )}
      />
      <section className="relative overflow-hidden volcano-gradient grain text-white">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-14 relative z-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {commune.region} · La Réunion</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold leading-none mb-3" data-testid="commune-title">{commune.name}</h1>
          <p className="text-white/85 max-w-lg" data-testid="commune-intro">Alertes, événements, bons plans et infos publiés par les habitants de la commune.</p>
          <div className="flex gap-6 mt-8 font-mono text-xs uppercase tracking-wider text-white/80">
            <span><strong className="block font-display text-2xl text-white">{posts.length}</strong>publications</span>
            <span><strong className="block font-display text-2xl text-white">{byCategory.alerte.length}</strong>alertes</span>
            <span><strong className="block font-display text-2xl text-white">{byCategory.evenement.length}</strong>événements</span>
          </div>
        </div>
      </section>

      <main className="container mx-auto max-w-3xl px-4 sm:px-6 py-6 space-y-6">
        <nav className="grid grid-cols-4 gap-2" aria-label="Catégories" data-testid="commune-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs sm:text-sm font-medium transition-colors ${tab === t.id ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40'}`}
              data-testid={`commune-tab-${t.id}`}
            >
              <t.icon className={`h-5 w-5 ${t.color}`} />
              <span>{t.label}</span>
              <span className="text-[10px] text-muted-foreground">{byCategory[t.id].length}</span>
            </button>
          ))}
        </nav>

        {loading ? (
          <div className="py-16 flex justify-center"><div className="spinner" /></div>
        ) : list.length === 0 ? (
          <Card className="border-dashed" data-testid="commune-empty">
            <CardContent className="py-12 text-center text-muted-foreground">
              Aucune publication dans cette catégorie pour {commune.name} pour le moment.
            </CardContent>
          </Card>
        ) : (
          <section className="space-y-3" data-testid="commune-posts">
            {list.map((post) => (
              <Card
                key={post.post_id}
                className="post-card cursor-pointer hover:border-primary/40 transition-colors"
                onClick={() => navigate(`/post/${post.post_id}`)}
                data-testid={`commune-post-${post.post_id}`}
              >
                <CardContent className="p-4 flex gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={post.user_picture} />
                        <AvatarFallback className="text-[10px]">{post.user_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-muted-foreground truncate">{post.user_name}{post.quartier ? ` · ${post.quartier}` : ''}</span>
                    </div>
                    <h2 className="font-semibold leading-snug line-clamp-2">
                      <Link to={`/post/${post.post_id}`} onClick={(e) => e.stopPropagation()}>{post.title}</Link>
                    </h2>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{post.content}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      {post.event_date && (
                        <Badge variant="secondary" className="text-[10px]">
                          {format(new Date(post.event_date), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                        </Badge>
                      )}
                      <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: fr })}</span>
                    </div>
                  </div>
                  {post.image_url && (
                    <img src={post.image_url} alt={post.title} className="h-20 w-20 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                  )}
                </CardContent>
              </Card>
            ))}
          </section>
        )}

        <section className="pt-4 border-t border-border" data-testid="commune-nearby">
          <h2 className="text-base md:text-lg font-bold mb-3">Autres communes · {commune.region}</h2>
          <div className="flex flex-wrap gap-2">
            {nearby.map((c) => (
              <Link
                key={c.id}
                to={`/commune/${c.slug}`}
                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary transition-colors"
                data-testid={`nearby-${c.slug}`}
              >
                {c.name} <ArrowRight className="h-3 w-3" />
              </Link>
            ))}
          </div>
        </section>
      </main>

      {user && <BottomNav />}
    </div>
  );
};

export default CommunePage;
